import type { ICustomerQuery } from "@repo/shopify/queries/customer-gql";
import type { ICaaCustomer } from "@repo/shopify/queries/customer-account-gql";
import type { CustomerAccessToken } from "@shopify/hydrogen-react/storefront-api-types";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

/** Customer Account API(OAuth) 토큰. jp locale 전용 — classic `token`과 별개로 관리 */
export interface ICaaToken {
  accessToken: string;
  refreshToken: string;
  idToken: string;
  /** ISO 문자열. Date.now() + expires_in * 1000 */
  expiresAt: string;
}

export interface ICustomerStore {
  token: CustomerAccessToken;
  caaToken: ICaaToken | null;
  customer: ICustomerQuery["customer"];
  /** Customer Account API(jp)로 조회한 원본 customer. classic `customer`와 스키마가 달라 별도 보관 */
  caaCustomer: ICaaCustomer | null;
  countryCode: string;

  login: (token: CustomerAccessToken) => void;
  loginCaa: (caaToken: ICaaToken) => void;
  logout: () => void;
  update: (customer: ICustomerQuery["customer"]) => void;
  updateCaa: (caaCustomer: ICaaCustomer) => void;
  setCountryCode: (countryCode: string) => void;
}

const initCustomer = {
  token: {
    accessToken: "",
    expiresAt: "",
  },
  caaToken: null as ICaaToken | null,
  caaCustomer: null as ICaaCustomer | null,
  customer: {
    acceptsMarketing: false,
    email: "",
    firstName: "",
    id: "",
    lastName: "",
    phone: "",
    tags: [],
    metafields: [],
    addresses: {
      edges: [],
      nodes: [],
      pageInfo: {
        endCursor: undefined,
        hasNextPage: false,
        hasPreviousPage: false,
        startCursor: undefined,
      },
    },
    numberOfOrders: "",
    createdAt: "",
    displayName: "",
    orders: {
      edges: [],
      nodes: [],
      pageInfo: {
        endCursor: undefined,
        hasNextPage: false,
        hasPreviousPage: false,
        startCursor: undefined,
      },
      totalCount: "",
    },
    gender: undefined,
    birth: undefined,
    wishlist: undefined,
    updatedAt: "",
  },
  countryCode: "",
};

const localLang = localStorage.getItem("i18nextLng") || "ko";

export const useCustomerStore = create<ICustomerStore>()(
  persist(
    (set) => ({
      // 초기값
      ...initCustomer,

      // 함수
      login: (token: CustomerAccessToken) => {
        set(() => ({ token }));
      },
      loginCaa: (caaToken: ICaaToken) => {
        set(() => ({ caaToken }));
      },
      logout: () => {
        set(() => initCustomer);
      },
      update: (customer: ICustomerQuery["customer"]) => {
        set((state) => ({ customer: { ...state.customer, ...customer } }));
      },
      updateCaa: (caaCustomer: ICaaCustomer) => {
        set(() => ({ caaCustomer }));
      },
      setCountryCode: (countryCode: string) => {
        set(() => ({ countryCode }));
      },
    }),
    {
      name: `${localLang}-customer`,
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
